/**
 * DesktopAvatarMenu — Avatar with a dropdown for Profile, Plan, Rewards, My Journey, Sign out.
 * Replaces the plain avatar-link in the desktop nav so plan status and shortcuts
 * are discoverable without crowding the top bar.
 */
import { useNavigate } from "react-router-dom";
import { Sparkles, User as UserIcon, Trophy, LogOut, ArrowRight, Camera } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useIsPremium } from "@/hooks/useIsPremium";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { UserAvatar } from "./UserAvatar";
import { cn } from "@/lib/utils";
import { useCreatorStatus } from "@/hooks/useCreatorStatus";


export function DesktopAvatarMenu() {
  const navigate = useNavigate();
  const { isPremium, loading } = useIsPremium();
  const creator = useCreatorStatus();

  const { user } = useAuth();

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Open account menu"
        className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <UserAvatar size="md" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
          </div>
        </DropdownMenuLabel>

        {!loading && (
          <button
            type="button"
            onClick={() => navigate("/profile?tab=account")}
            className={cn(
              "mx-1 my-1 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs w-[calc(100%-0.5rem)]",
              isPremium
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80"
            )}
          >
            <span className="inline-flex items-center gap-1.5 font-semibold">
              {isPremium ? (
                <>
                  <Sparkles className="h-3 w-3" /> Premium plan
                </>
              ) : (
                <>You're on Free</>
              )}
            </span>
            <span className="inline-flex items-center gap-0.5">
              {isPremium ? "Manage" : "Upgrade"}
              <ArrowRight className="h-3 w-3" />
            </span>
          </button>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => navigate("/profile")}>
          <UserIcon className="mr-2 h-4 w-4" /> My Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/my-progress")}>
          <Trophy className="mr-2 h-4 w-4" /> My Journey
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/rewards")}>
          <Sparkles className="mr-2 h-4 w-4" /> Rewards Hub
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(creator.to)} className="items-start">
          <Camera className="mr-2 mt-0.5 h-4 w-4" />
          <span className="flex flex-col">
            <span>{creator.menuLabel}</span>
            <span className="text-xs text-muted-foreground">{creator.statusLine}</span>
          </span>
        </DropdownMenuItem>


        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
